

import { formatDateTime } from "@/lib/dateFormat";
import { EventLog } from "./types";

interface EventActivityTimelineProps {
  logs: EventLog[];
}

const actionConfig: Record<string, { label: string; color: string; icon: string }> = {
  created: {
    label: "Event Created",
    color: "bg-emerald-500",
    icon: "M12 4v16m8-8H4",
  },
  updated: {
    label: "Event Updated",
    color: "bg-amber-500",
    icon: "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z",
  },
  status_changed: {
    label: "Status Changed",
    color: "bg-blue-500",
    icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
  },
  payment_added: {
    label: "Payment Received",
    color: "bg-violet-500",
    icon: "M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z",
  },
  vendor_payment_added: {
    label: "Vendor Payment",
    color: "bg-sky-500",
    icon: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z",
  },
  service_added: {
    label: "Service Added",
    color: "bg-teal-500",
    icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4",
  },
  service_removed: {
    label: "Service Removed",
    color: "bg-orange-400",
    icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9h6",
  },
  vendor_added: {
    label: "Vendor Added",
    color: "bg-indigo-500",
    icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z",
  },
  vendor_removed: {
    label: "Vendor Removed",
    color: "bg-rose-400",
    icon: "M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6",
  },
  converted_from_quotation: {
    label: "Converted from Quotation",
    color: "bg-purple-500",
    icon: "M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4",
  },
  deleted: {
    label: "Event Deleted",
    color: "bg-rose-500",
    icon: "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16",
  },
};

// Local formatter for activity timeline — always shows full amounts
function formatAmount(val: any) {
  const n = Number(val);
  if (isNaN(n)) return val;
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function ChangePills({ action, changes }: { action: string; changes: Record<string, any> }) {
  if (action === "status_changed") {
    return (
      <div className="mt-1 flex items-center gap-1.5 text-xs">
        <span className="px-1.5 py-0.5 bg-stone-100 rounded text-stone-600 font-medium capitalize">
          {changes.old_status}
        </span>
        <svg className="w-3 h-3 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span className="px-1.5 py-0.5 bg-blue-100 rounded text-blue-700 font-medium capitalize">
          {changes.new_status}
        </span>
      </div>
    );
  }

  if (action === "payment_added" && changes.amount) {
    return (
      <div className="mt-1 flex flex-wrap gap-1.5 text-xs">
        <span className="px-1.5 py-0.5 bg-violet-100 rounded text-violet-700 font-semibold">
          {formatAmount(changes.amount)}
        </span>
        {changes.payment_method && (
          <span className="px-1.5 py-0.5 bg-stone-100 rounded text-stone-600 capitalize">
            {String(changes.payment_method).replace(/_/g, " ")}
          </span>
        )}
        {changes.payment_date && (
          <span className="px-1.5 py-0.5 bg-stone-100 rounded text-stone-500">
            {changes.payment_date}
          </span>
        )}
      </div>
    );
  }

  if ((action === "service_added" || action === "service_removed") && changes.unit_price) {
    return (
      <div className="mt-1 flex flex-wrap gap-1.5 text-xs">
        <span className="px-1.5 py-0.5 bg-teal-100 rounded text-teal-700 font-semibold">
          {formatAmount(changes.unit_price)} × {changes.quantity ?? 1}
        </span>
        {changes.cost_price && (
          <span className="px-1.5 py-0.5 bg-stone-100 rounded text-stone-500">
            Cost: {formatAmount(changes.cost_price)}
          </span>
        )}
      </div>
    );
  }

  if ((action === "vendor_added" || action === "vendor_removed") && changes.unit_price) {
    return (
      <div className="mt-1 flex flex-wrap gap-1.5 text-xs">
        <span className="px-1.5 py-0.5 bg-indigo-100 rounded text-indigo-700 font-semibold">
          {formatAmount(changes.unit_price)} × {changes.quantity ?? 1}
        </span>
        {changes.commission_value > 0 && (
          <span className="px-1.5 py-0.5 bg-stone-100 rounded text-stone-600">
            Commission: {changes.commission_value}{changes.commission_type === "percentage" ? "%" : " ₹"}
          </span>
        )}
      </div>
    );
  }

  if (action === "converted_from_quotation") {
    return (
      <div className="mt-1 flex flex-wrap gap-1.5 text-xs">
        {changes.quotation_number && (
          <span className="px-1.5 py-0.5 bg-purple-100 rounded text-purple-700 font-semibold">
            {changes.quotation_number}
          </span>
        )}
        {changes.advance_payment > 0 && (
          <span className="px-1.5 py-0.5 bg-violet-100 rounded text-violet-700">
            Advance: {formatAmount(changes.advance_payment)}
          </span>
        )}
        {changes.services_count > 0 && (
          <span className="px-1.5 py-0.5 bg-teal-100 rounded text-teal-700">
            {changes.services_count} service{changes.services_count !== 1 ? "s" : ""}
          </span>
        )}
        {changes.vendors_count > 0 && (
          <span className="px-1.5 py-0.5 bg-indigo-100 rounded text-indigo-700">
            {changes.vendors_count} vendor{changes.vendors_count !== 1 ? "s" : ""}
          </span>
        )}
      </div>
    );
  }

  return null;
}

export default function EventActivityTimeline({ logs }: EventActivityTimelineProps) {
  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
      <div className="px-4 py-3 bg-stone-50 border-b border-stone-200">
        <h3 className="font-bold text-stone-800 text-sm flex items-center gap-2">
          <svg className="w-4 h-4 text-stone-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Activity Log
          <span className="ml-1 text-xs bg-stone-200 text-stone-600 px-1.5 py-0.5 rounded-full font-medium">
            {logs.length}
          </span>
        </h3>
      </div>

      {logs.length === 0 ? (
        <div className="p-6 text-center text-stone-400 text-sm">No activity recorded yet</div>
      ) : (
        <div className="p-4 space-y-3">
          {logs.map((log, idx) => {
            const config = actionConfig[log.action] ?? {
              label: log.action.replace(/_/g, " "),
              color: "bg-stone-400",
              icon: "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
            };

            return (
              <div key={log.id} className="flex gap-3">
                {/* Timeline dot + line */}
                <div className="flex flex-col items-center">
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1 ${config.color}`} />
                  {idx !== logs.length - 1 && (
                    <div className="w-px flex-1 bg-stone-200 mt-1 min-h-[20px]" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 pb-3 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-stone-800 capitalize">{config.label}</p>
                      <p className="text-xs text-stone-500 truncate">by {log.user_name}</p>
                    </div>
                    <span className="text-xs text-stone-400 flex-shrink-0 whitespace-nowrap">
                      {formatDateTime(log.created_at)}
                    </span>
                  </div>

                  {log.description && (
                    <div className="mt-1.5 px-2.5 py-1.5 bg-stone-50 rounded-lg border-l-2 border-stone-300">
                      <p className="text-xs text-stone-700">{log.description}</p>
                    </div>
                  )}

                  {log.changes && (
                    <ChangePills action={log.action} changes={log.changes} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
